import PageHeader from 'shared/components/page-header/page-header';
import SharedTable from 'shared/components/table/table';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import { useCollection } from 'core/hooks/useCollection';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import { PageElement } from 'core/utilities/interface-helpers';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Checkbox from '@atlaskit/checkbox';
import SharedFilters, { Filter, SelectedValue } from 'shared/components/filters/filters';
import { deleteItems } from 'core/utilities/api-wrapper';
import ConfirmationDialog from 'modules/dialogs/confirmation-dialog';
import ExportDialog from 'modules/dialogs/export-dialog';
import RectifyRunActivityDialog from 'modules/dialogs/rectify-run-activity-dialog';
import { useAuthState } from 'core/providers/AuthProvider';
import { Role } from 'core/config/roles';
import { useFlags } from '@atlaskit/flag';
import { orderBy, QueryConstraint, Timestamp, where } from 'firebase/firestore';
import { IButton } from 'shared/components/buttons/button-interface';
import { FirebaseFunctionNames } from 'core/constants/firebase-function-names';
import dayjs from 'dayjs';
import { IRunActivityDao } from 'core/api/types/run-activity.interface';
import { RunActivityApiService } from 'core/api';
import { useNavigate } from 'react-router-dom';
import { useSharedData } from 'core/providers/SharedDataProvider';

const RunActivityTableColumns: ISharedTableColumn[] = [
  { label: '', key: 'select', templateId: 'select', width: 1, isCompact: true },
  { label: 'Date', key: 'date', templateId: 'date', width: 15 },
  { label: 'Started', key: 'started', templateId: 'startTime', width: 15 },
  { label: 'Ended', key: 'ended', templateId: 'endTime', width: 15 },
  { label: 'Run Name', key: 'name', templateId: 'text', width: 15 },
  { label: 'Driver', key: 'currentDriver', templateId: 'currentDriver', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const RunActivityList = () => {
  const dialog = useDialog();
  const flags = useFlags();
  const navigate = useNavigate();

  const { drivers, runs } = useSharedData();
  const { userRoles } = useAuthState();
  const isSuperAdmin = userRoles?.includes(Role.SUPER_ADMIN) ?? false;

  const [filterQuery, setFilterQuery] = useState<SelectedValue[]>([]);
  const [constraints, setConstraints] = useState<QueryConstraint[]>([]);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const { tableRows, loading, loadMore, hasMore, loadingMore } = useCollection<IRunActivityDao>({
    apiService: RunActivityApiService,
    constraints: [...constraints, orderBy('created.at', 'desc')],
  });

  useEffect(() => {
    setSelectedRows([]);
  }, [tableRows]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const newConstraints = filterQuery
        .filter((filter) => filter.value !== '')
        .map((filter) => {
          if (filter.id === 'driver') filter.id = 'lastDriver.uid';
          if (filter.id.includes('From')) {
            const date = Timestamp.fromDate(
              dayjs(filter.value as string)
                .startOf('day')
                .toDate()
            );
            return where('created.at', '>=', date);
          } else if (filter.id.includes('To')) {
            const date = Timestamp.fromDate(
              dayjs(filter.value as string)
                .endOf('day')
                .toDate()
            );
            return where('created.at', '<=', date);
          }

          const value = filter.value === 'true' ? true : filter.value === 'false' ? false : filter.value;
          return where(filter.id, '==', value);
        });

      setConstraints(newConstraints);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterQuery]);

  const handleFilterChange = useCallback(
    (values: SelectedValue[]) => {
      const currentSerialized = JSON.stringify(filterQuery.sort((a, b) => a.id.localeCompare(b.id)));
      const newSerialized = JSON.stringify(values.sort((a, b) => a.id.localeCompare(b.id)));

      if (currentSerialized !== newSerialized) {
        setFilterQuery(values);
      }
    },
    [filterQuery]
  );

  const rowChecked = (event: ChangeEvent<HTMLInputElement>, uid: string) => {
    if (event.target.checked) {
      setSelectedRows((prevState) => [...prevState, uid]);
    } else {
      setSelectedRows((prevState) => prevState.filter((selectedUid) => selectedUid !== uid));
    }
  };

  const selectCellTemplate = (runActivity: IRunActivityDao) => {
    return (
      <Checkbox isChecked={selectedRows.includes(runActivity.uid)} onChange={(e) => rowChecked(e, runActivity.uid)} />
    );
  };

  const driverCellTemplate = (runActivity: IRunActivityDao) => {
    return <p>{runActivity.lastDriver?.name || '-'}</p>;
  };

  const startTimeTemplate = (runActivity: IRunActivityDao) => {
    const firstDriver = runActivity.driverHistory?.slice().sort((a, b) => a.start.toMillis() - b.start.toMillis())[0];
    return <p>{firstDriver ? dayjs(firstDriver.start.toDate()).format('HH:mm') : '-'}</p>;
  };

  const endTimeTemplate = (runActivity: IRunActivityDao) => {
    const lastDriver = runActivity.driverHistory?.slice().sort((a, b) => b.start.toMillis() - a.start.toMillis())[0];
    return <p>{lastDriver && lastDriver.end ? dayjs(lastDriver.end.toDate()).format('HH:mm') : '-'}</p>;
  };

  const dateTemplate = (runActivity: IRunActivityDao) => {
    return <p>{dayjs(runActivity.created.at.toDate()).format('DD/MM/YYYY')}</p>;
  };

  const actionCellTemplate = (runActivity: IRunActivityDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => navigate(runActivity.uid)}
        type='button'
        spacing='compact'
        appearance='subtle'
        label='View'
      />
    </div>
  );

  const customTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: actionCellTemplate,
      id: 'action',
    },
    { template: selectCellTemplate, id: 'select' },
    { template: driverCellTemplate, id: 'currentDriver' },
    { template: startTimeTemplate, id: 'startTime' },
    { template: endTimeTemplate, id: 'endTime' },
    { template: dateTemplate, id: 'date' },
  ];

  const headerActions: PageElement[] = [
    {
      key: 'exportRunActivity',
      element: (
        <SharedButton
          onClick={() =>
            dialog?.openDialog(
              <ExportDialog
                title='Export Run Activity'
                apiService={RunActivityApiService}
                functionName={FirebaseFunctionNames.EXPORT_RUN_ACTIVITY}
                filterQuery={filterQuery}
              />
            )
          }
          type='button'
          appearance='default'
          label='Export Run Activity'
        />
      ),
    },
    ...(selectedRows.length
      ? [
          {
            key: 'deleteRunActivity',
            element: (
              <SharedButton
                onClick={() =>
                  dialog?.openDialog(
                    <ConfirmationDialog
                      title='Delete run activity'
                      textContent='Are you sure you want to delete this run activity? This action cannot be undone.'
                      action={() => deleteItems(RunActivityApiService, selectedRows, flags, dialog)}
                      buttonLabel='Delete Run Activity'
                    />
                  )
                }
                type='button'
                appearance='danger'
                label='Delete Run Activity'
              />
            ),
          },
        ]
      : []),
    ...(process.env.NODE_ENV === 'development'
      ? [
          {
            key: 'forceCalculateRunActivity',
            element: (
              <SharedButton
                onClick={async () =>
                  await RunActivityApiService.callFunction(
                    {},
                    FirebaseFunctionNames.FORCE_CALCULATE_TOMORROWS_RUN_ACTIVITY
                  )
                }
                type='button'
                appearance='danger'
                label='Force Calculate Run Activity'
              />
            ),
          },
        ]
      : []),
    ...(isSuperAdmin
      ? [
          {
            key: 'rectifyRunActivity',
            element: (
              <SharedButton
                onClick={() => dialog?.openDialog(<RectifyRunActivityDialog />)}
                type='button'
                appearance='warning'
                label='Rectify Run Activity'
              />
            ),
          },
        ]
      : []),
  ];

  const filters: Filter[] = [
    {
      id: 'dateFrom',
      label: 'Date From',
      date: true,
    },
    {
      id: 'dateTo',
      label: 'Date To',
      date: true,
    },
    {
      id: 'runId',
      label: 'Run',
      options: runs?.map((run) => ({ value: run.uid, label: run.name })) || [],
    },
    {
      id: 'driver',
      label: 'Driver',
      options: drivers?.map((driver) => ({ value: driver.uid, label: driver.fullName })) || [],
    },
  ];

  const loadMoreButton: IButton = {
    onClick: () => loadMore(),
    type: 'submit',
    appearance: 'subtle',
    label: 'Load more',
    isLoading: loadingMore,
  };

  return (
    <>
      <PageHeader title='Run Activity' actions={headerActions} />
      <SharedFilters filters={filters} onFilterValueChange={handleFilterChange} />
      <SharedTable
        columns={RunActivityTableColumns}
        rows={tableRows}
        isLoading={loading}
        customTemplates={customTemplates}
        loadMoreButton={hasMore ? loadMoreButton : undefined}
      />
    </>
  );
};

export default RunActivityList;
